import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Table from '../common/Table';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Card from '../common/Card';
import api from '../../services/api';
import '../../styles/category-list.css';

const CategoryList = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    category_name: '',
    description: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const response = await api.get('/categories.php');
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      category_name: category.category_name,
      description: category.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      await api.delete(`/categories.php/${id}`);
      loadCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert(error.response?.data?.error || 'Failed to delete category');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category_name.trim()) {
      setErrors({ category_name: 'Category name is required' });
      return;
    }

    try {
      if (editingCategory) {
        await api.put(`/categories.php/${editingCategory.id}`, formData);
      } else {
        await api.post('/categories.php', formData);
      }
      
      setShowModal(false);
      setFormData({ category_name: '', description: '' });
      setEditingCategory(null);
      loadCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      alert(error.response?.data?.error || 'Failed to save category');
    }
  };

  const columns = [
    {
      key: 'category_name',
      label: 'Category Name'
    },
    {
      key: 'description',
      label: 'Description'
    },
    {
      key: 'product_count',
      label: 'Products',
      render: (value) => value || 0
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div className="action-buttons">
          <button 
            className="icon-btn edit"
            onClick={() => handleEdit(row)}
          >
            ✏️
          </button>
          <button 
            className="icon-btn delete"
            onClick={() => handleDelete(row.id)}
            disabled={row.product_count > 0}
            title={row.product_count > 0 ? 'Cannot delete category with products' : ''}
          >
            🗑️
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="category-list">
      <Card>
        <div className="category-list-header">
          <h2>Product Categories</h2>
          <div className="header-actions" style={{ display: 'flex', gap: '10px' }}>
            <Button 
              variant="secondary"
              onClick={() => navigate('/products/categories/bulk-upload')}
              icon="📥"
            >
              Bulk Upload
            </Button>
            <Button 
              variant="primary"
              onClick={() => {
                setEditingCategory(null);
                setFormData({ category_name: '', description: '' });
                setShowModal(true);
              }}
              icon="➕"
            >
              Add Category
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          data={categories}
          loading={loading}
        />

        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editingCategory ? 'Edit Category' : 'Add Category'}
        >
          <form onSubmit={handleSubmit} className="category-form">
            <Input
              label="Category Name"
              value={formData.category_name}
              onChange={(e) => setFormData({ ...formData, category_name: e.target.value })}
              error={errors.category_name}
              required
              autoFocus
            />
            
            <Input
              label="Description"
              type="textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
            />

            <div className="form-actions">
              <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Modal>
      </Card>
    </div>
  );
};

export default CategoryList;